import debounce from 'lodash/debounce';
import {useSelector} from 'react-redux';
import {useHistory} from 'react-router';
import {unwrapResult} from '@reduxjs/toolkit';
import {FormattedMessage, useIntl} from 'react-intl';
import {Box, Button, Toolbar} from '@material-ui/core';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {Navigation} from '../Navigation';
import {useMergeData} from './useMergeData';
import {RootState, useAppDispatch} from '../../../store';
import {DeploymentDataReview} from '../DeploymentDataReview/DeploymentDataReview';
import {
  dataPrepareActions,
  loadAnnotations,
  saveAnnotations,
} from '../../../store/dataPrepare';
import {
  DataPrepareType,
  ImageType,
} from '../../../types/dataset/DatasetGetAnnotationsResponse';

const {setStatus, setDataPrep} = dataPrepareActions;

const DEBOUNCE_DELAY = 1400;

type DeploymentDataViewProps = {
  projectId: string;
  deploymentId?: string;
  inProgress: boolean;
};

export const DeploymentDataView = ({
  projectId,
  inProgress,
  deploymentId = '',
}: DeploymentDataViewProps) => {
  const history = useHistory();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const annotationData = useSelector(
    (state: RootState) => state.dataPrepare.annotationData
  );

  const [isDebouncing, setIsDebouncing] = useState(false);
  const [localAnnotationData, setLocalAnnotationData] = useState(annotationData);

  useEffect(
    function updateLocalAnnotationData() {
      if (annotationData) {
        setLocalAnnotationData(annotationData);
      }
    },
    [annotationData]
  );

  const deploymentIdRef = useRef(deploymentId);
  const projectIdRef = useRef(projectId);

  useEffect(
    function updateDeploymentIdRef() {
      deploymentIdRef.current = deploymentId;
    },
    [deploymentId]
  );

  useEffect(
    function updateProjectIdRef() {
      projectIdRef.current = projectId;
    },
    [projectId]
  );

  const saveAnnotationsDebounced = useMemo(() => {
    return debounce(async updatedAnnotations => {
      await dispatch(
        saveAnnotations({
          resourceId: deploymentIdRef.current,
          projectId: projectIdRef.current,
          updatedAnnotations,
        })
      );

      setIsDebouncing(false);
    }, DEBOUNCE_DELAY);
  }, [dispatch]);

  useEffect(
    function triggerSaveAnnotationsOnDataChange() {
      if (localAnnotationData) {
        setIsDebouncing(true);
        saveAnnotationsDebounced(localAnnotationData);
      }
    },
    [localAnnotationData, saveAnnotationsDebounced]
  );

  useEffect(
    function cleanupOnUnmount() {
      return () => {
        saveAnnotationsDebounced.cancel();
      };
    },
    [saveAnnotationsDebounced]
  );

  const handleSelect = useCallback(
    (id: string) => {
      if (localAnnotationData) {
        const updatedAnnotations = {
          ...localAnnotationData,
          images: localAnnotationData.images.map<ImageType>(image => {
            if (image.id === Number(id)) {
              return {
                ...image,
                selected: image.selected === 1 ? 0 : 1,
              };
            }
            return image;
          }),
        };

        setLocalAnnotationData(updatedAnnotations);
      }
    },
    [localAnnotationData]
  );

  const handleSelectAll = useCallback(() => {
    if (localAnnotationData) {
      const updatedAnnotations = {
        ...localAnnotationData,
        images: localAnnotationData.images.map<ImageType>(image => ({
          ...image,
          selected: 1,
        })),
      };

      setLocalAnnotationData(updatedAnnotations);
    }
  }, [localAnnotationData]);

  const handleClearAll = useCallback(() => {
    if (localAnnotationData) {
      const updatedAnnotations = {
        ...localAnnotationData,
        images: localAnnotationData.images.map<ImageType>(image => ({
          ...image,
          selected: 0,
        })),
      };

      setLocalAnnotationData(updatedAnnotations);
    }
  }, [localAnnotationData]);

  const dataToShow = useMemo(
    () =>
      localAnnotationData?.images?.map(image => ({
        id: image.id.toString(),
        name: image.file_name,
        size: image.size,
        selected: Boolean(image.selected),
        lastModified: new Date(image.date_captured),
      })) || [],
    [localAnnotationData]
  );

  const isValid = useMemo(
    () => localAnnotationData?.images?.some(image => image.selected),
    [localAnnotationData]
  );

  const disableForward = useMemo(() => !isValid || inProgress || isDebouncing, [
    isValid,
    inProgress,
    isDebouncing,
  ]);

  const initiateMergeData = useMergeData({
    projectId,
    onResume: () => dispatch(setStatus('LOADING')),
    onStart: () => dispatch(setStatus('LOADING')),
    onFinish: async dataPreparationContainer => {
      const newDatasetId = dataPreparationContainer?.id;
      if (newDatasetId) {
        const action = await dispatch(loadAnnotations({projectId, origin: 'raw'}));
        const rawAnnotationData = unwrapResult(action);
        const type = rawAnnotationData.annotations.some(annotation => annotation.bbox)
          ? DataPrepareType.DETECTION
          : DataPrepareType.CLASSIFICATION;

        await dispatch(
          saveAnnotations({
            resourceId: newDatasetId,
            projectId,
            updatedAnnotations: {
              ...rawAnnotationData,
              info: {...rawAnnotationData.info, type},
            },
          })
        );
        dispatch(setDataPrep({container: dataPreparationContainer, context: 'raw'}));
        dispatch(setStatus('IDLE'));
        history.push(`/project/${projectId}/data/prepare/raw?resume=true`);
      }
    },
    onError: () => dispatch(setStatus('IDLE')),
  });

  const handleForward = useCallback(() => {
    if (localAnnotationData) {
      initiateMergeData({...localAnnotationData, categories: []});
    }
  }, [localAnnotationData, initiateMergeData]);

  return (
    <>
      <Toolbar className="data-prepare__header" variant="dense">
        <Button
          className="data-prepare__previous-btn"
          variant="contained"
          disableElevation
          onClick={() => history.goBack()}
        >
          <FormattedMessage id="form.returnToPreviousPage" />
        </Button>
        <Box flexGrow={1}></Box>
        <Navigation onForward={handleForward} disableForward={disableForward} hideBack />
      </Toolbar>
      <DeploymentDataReview
        title={intl.formatMessage({id: 'dataPrep.select.images'})}
        projectId={projectId}
        fileItems={dataToShow}
        onClearAll={handleClearAll}
        onSelectAll={handleSelectAll}
        onSelect={id => handleSelect(id)}
      />
    </>
  );
};
